"use client";

import Image from "next/image";
import { useCallback, useMemo } from "react";

import { useAuth } from "@/core/auth/client/AuthProvider";
import { DEFAULT_CASINO_VIEW, type CasinoView } from "@/domain/casino/constants";
import CasinoSection from "@/features/v2/home/components/CasinoSection/CasinoSection";
import { CasinoSectionType } from "@/features/v2/home/components/CasinoSection/enums";
import instanceId from "@/features/v2/home/components/CasinoSection/intance";
import { CasinoGame } from "@/features/v2/home/components/CasinoSection/interface";

import GameGrid from "./components/GameGrid";
import { useGames } from "./hooks/useGames";
import { core } from "./localization/core";

function GamesView({ currentView, initialCasinoGames, initialZingaGames }: { currentView: CasinoView; initialCasinoGames?: CasinoGame[]; initialZingaGames?: CasinoGame[] }) {
  const { session } = useAuth();
  const { games, loading, error, hasMore, totalCount, loadMore, refetch } = useGames();

  const isParaTiView = currentView === DEFAULT_CASINO_VIEW;

  const type = useMemo(() => {
    return session?.user ? CasinoSectionType.ZINGA : CasinoSectionType.CASINO;
  }, [session]);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 text-lg mb-4">{core.onError.title}</div>
        <p className="text-default-600 text-sm mb-4">{error}</p>
        <button type="button" onClick={handleRetry} className="bg-hprimary-500 hover:bg-hprimary-600 text-white px-4 py-2 rounded-lg transition-colors">
          {core.onError.button}
        </button>
      </div>
    );
  }

  if (isParaTiView) {
    return (
      <div className="w-full py-6 max-w-7xl mx-auto">
        <CasinoSection type={type} showLink={false} instanceId={instanceId.HOME_ZINGA} initialGames={type === CasinoSectionType.ZINGA ? initialZingaGames : initialCasinoGames} />
        {session && <CasinoSection type={CasinoSectionType.FAVORITES} showLink={false} instanceId={instanceId.HOME_FAVORITES} />}
        <div className="mt-8 mb-6 mx-4 flex items-center gap-2">
          <Image src="/img/v2/icons/Play.webp" alt="Suggested" width={30} height={30} />
          <h2 className="text-xl font-semibold text-default-900 ">{core.games.suggestedTitle}</h2>
        </div>
        <div className="mx-4">
          <GameGrid games={games} loading={loading} hasMore={hasMore} onLoadMore={loadMore} />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-default-900 mb-2">{core.games.title}</h2>
        {!loading && <p className="text-default-600 text-sm">{totalCount > 0 ? `${totalCount} ${core.games.findGames}` : core.games.description}</p>}
      </div>

      <GameGrid games={games} loading={loading} hasMore={hasMore} onLoadMore={loadMore} />
    </div>
  );
}

export default GamesView;
